import {
	Content,
	type PopoverContentProps,
	Portal,
	Root,
	Trigger
} from '@radix-ui/react-popover'
import type { CSSProperties } from 'react'
import { type FC, type ReactNode, useMemo, useState } from 'react'
import {
	AlertTriangleIcon,
	FileX02Icon,
	SearchLgIcon
} from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { EmptyFlagsResult } from '@/features/photoset/components/flags-popover/empty-flags-result'
import { FlagsPopoverItem } from '@/features/photoset/components/flags-popover/flag-popover-item'
import { useGetFlags } from '@/features/photoset/queries/use-get-flags'
import type { RemoveImagesFlagData } from '@/features/photoset/queries/use-remove-images-flag'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type {
	Flag,
	FlagWithLabel,
	Image,
	Photoset,
	PhotosetSpace
} from '@/features/photoset/types'
import { raise } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

const FlagsPopoverContent = ({
	isError,
	isFetching,
	flagsList,
	search,
	setSearch,
	stepId,
	selectedImagesIds,
	photosetSpaceIds,
	isUpdateDisabled,
	hideDescriptions,
	onRemoveFlagCb
}: {
	isError: boolean
	isFetching: boolean
	flagsList: {
		appliedAllFlags: FlagWithLabel[]
		appliedSomeFlags: FlagWithLabel[]
		otherFlags: FlagWithLabel[]
	}
	search: string
	setSearch: (value: string) => void
	stepId: Photoset['step_id']
	selectedImagesIds: Image['id'][]
	photosetSpaceIds?: PhotosetSpace['id'][]
	isUpdateDisabled: boolean
	hideDescriptions: boolean
	onRemoveFlagCb?: (data: RemoveImagesFlagData) => void
}) => {
	const { t } = useTranslation()
	if (isError) {
		return (
			<EmptyFlagsResult
				hasRetry
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	if (
		flagsList.appliedAllFlags.length === 0 &&
		flagsList.appliedSomeFlags.length &&
		flagsList.otherFlags.length === 0
	) {
		return (
			<EmptyFlagsResult
				hasRetry
				icon={<FileX02Icon />}
				title={t('common:no_results')}
			/>
		)
	}
	if (isFetching) {
		return (
			<EmptyFlagsResult
				hasRetry={false}
				icon={<Spinner className='fill-neutral-900' size={16} />}
				title={t('common:loading')}
			/>
		)
	}
	return (
		<>
			<div className='flex items-center gap-2 px-3.5 py-3'>
				<SearchLgIcon size={20} />
				<input
					className='w-full text-base'
					onChange={(e) => setSearch(e.currentTarget.value)}
					placeholder={t('photoset:search_flags')}
					type='text'
					value={search}
				/>
			</div>

			<Separator orientation='horizontal' />

			<ScrollArea scrollbar={<Scrollbar />}>
				<div className='h-[280px]'>
					{(flagsList.appliedAllFlags.length > 0 ||
						flagsList.appliedSomeFlags.length > 0) && (
						<>
							<div className='p-1'>
								<span className='block px-3 py-1 font-medium text-neutral-500 text-xs'>
									{t('common:applied')}
								</span>

								{flagsList.appliedAllFlags.map((flag) => (
									<FlagsPopoverItem
										defaultChecked
										flag={flag}
										hideDescription={hideDescriptions}
										isUpdateDisabled={isUpdateDisabled}
										key={flag.id}
										onRemoveFlagCb={onRemoveFlagCb}
										photosetSpaceIds={photosetSpaceIds}
										selectedImagesIds={selectedImagesIds}
										stepId={stepId}
									/>
								))}

								{flagsList.appliedSomeFlags.map((flag) => (
									<FlagsPopoverItem
										flag={flag}
										hideDescription={hideDescriptions}
										isIndeterminate
										isUpdateDisabled={isUpdateDisabled}
										key={flag.id}
										onRemoveFlagCb={onRemoveFlagCb}
										photosetSpaceIds={photosetSpaceIds}
										selectedImagesIds={selectedImagesIds}
										stepId={stepId}
									/>
								))}
							</div>

							<Separator orientation='horizontal' />
						</>
					)}

					<div className='p-1'>
						{flagsList.otherFlags.map((flag) => (
							<FlagsPopoverItem
								flag={flag}
								hideDescription={hideDescriptions}
								isUpdateDisabled={isUpdateDisabled}
								key={flag.id}
								onRemoveFlagCb={onRemoveFlagCb}
								photosetSpaceIds={photosetSpaceIds}
								selectedImagesIds={selectedImagesIds}
								stepId={stepId}
							/>
						))}
					</div>
				</div>
			</ScrollArea>
		</>
	)
}

interface FlagsPopoverProps {
	trigger: ReactNode
	selectedImagesIds: Image['id'][]
	disableScrollBelow?: boolean
	stepId: Photoset['step_id']
	types?: Flag['type'][]
	photosetSpaceIds?: PhotosetSpace['id'][]
	align?: PopoverContentProps['align']
	isUpdateDisabled?: boolean
	hideDescriptions?: boolean
	onRemoveFlagCb?: (data: RemoveImagesFlagData) => void
	width?: CSSProperties['width']
	triggerWidth?: CSSProperties['width']
}

export const FlagsPopover: FC<FlagsPopoverProps> = ({
	trigger,
	selectedImagesIds,
	stepId,
	disableScrollBelow = false,
	types = ['metadata', 'visual_metadata', 'visual'],
	align = 'start',
	photosetSpaceIds,
	isUpdateDisabled = false,
	hideDescriptions = false,
	onRemoveFlagCb,
	width = '320px',
	triggerWidth
}) => {
	const [search, setSearch] = useState('')
	const { t } = useTranslation()
	const { images } = usePhotosetStore()

	const flagsQuery = useGetFlags({
		params: {
			types,
			response_type: 'extended'
		}
	})

	const flagsList = useMemo(() => {
		const appliedAllFlags: FlagWithLabel[] = []
		const appliedSomeFlags: FlagWithLabel[] = []
		const otherFlags: FlagWithLabel[] = []
		const selectedImages = (
			selectedImagesIds ?? raise('No image selected')
		).map((imageId) => images.get(imageId))

		const hasFlag = (
			image: Image | null | undefined,
			flag: Flag
		): boolean => {
			if (!image) {
				return false
			}

			const hasImageFlag = image.flags?.some(
				(imageFlag) => imageFlag.id === flag.id
			)
			const isFlagApproved = image.image_flags?.some(
				(imageFlag) =>
					imageFlag.flag_id === flag.id && imageFlag.is_flag_approved
			)

			return !!(hasImageFlag && isFlagApproved)
		}

		const searchFlagByLabel = (
			flags: FlagWithLabel[],
			inputSearch: string
		) =>
			flags.filter((flag) =>
				flag.label.toLowerCase().includes(inputSearch.toLowerCase())
			)

		if (flagsQuery.data) {
			for (const flag of flagsQuery.data) {
				if (selectedImages.every((image) => hasFlag(image, flag))) {
					appliedAllFlags.push({
						...flag,
						label: t(`photoset:flags.${flag.key_name}.label`)
					})
				} else if (
					selectedImages.some((image) => hasFlag(image, flag))
				) {
					appliedSomeFlags.push({
						...flag,
						label: t(`photoset:flags.${flag.key_name}.label`)
					})
				} else {
					otherFlags.push({
						...flag,
						label: t(`photoset:flags.${flag.key_name}.label`)
					})
				}
			}
		}

		return {
			appliedAllFlags: searchFlagByLabel(appliedAllFlags, search),
			appliedSomeFlags: searchFlagByLabel(appliedSomeFlags, search),
			otherFlags: searchFlagByLabel(otherFlags, search)
		}
	}, [images, flagsQuery, selectedImagesIds, search, t])

	return (
		<Root
			modal={disableScrollBelow}
			onOpenChange={(visibility) => visibility && setSearch('')}
		>
			<Trigger asChild>
				<div
					className='cursor-pointer'
					style={{ width: triggerWidth ?? 'fit-content' }}
				>
					{trigger}
				</div>
			</Trigger>

			<Portal>
				<Content
					align={align}
					className='z-(--z-modal) rounded-lg border border-neutral-200 bg-neutral-white py-1 shadow-lg'
					sideOffset={12}
					style={{ width }}
				>
					<FlagsPopoverContent
						flagsList={flagsList}
						hideDescriptions={hideDescriptions}
						isError={flagsQuery.isError}
						isFetching={flagsQuery.isFetching}
						isUpdateDisabled={isUpdateDisabled}
						onRemoveFlagCb={onRemoveFlagCb}
						photosetSpaceIds={photosetSpaceIds}
						search={search}
						selectedImagesIds={selectedImagesIds}
						setSearch={setSearch}
						stepId={stepId}
					/>
				</Content>
			</Portal>
		</Root>
	)
}
